import {
  ChangeEvent,
  ReactNode,
  createContext,
  useContext,
  useId,
  useState,
} from "react";

/**
 * Interface for the select card state
 */
interface ISelectCardState {
  /**
   * When `true`, adds the item to the selected state holder.
   */
  checked: boolean;

  /**
   * Gives an explicit id to each item in the set.
   */
  id?: string | number;
}

/**
 * Interface for the select card onChange options
 */
interface ISelectCardOnChangeOptions {
  /**
   * When `true`, doesn't fire the user's `onChange`
   */
  internal?: boolean;
}

/**
 * Interface for the select card context
 */
export interface ISelectCardContext {
  /**
   * Callback function that is triggered when the selection state changes.
   *
   * @callback OnChangeCallback
   * @param {ChangeEvent<HTMLInputElement>} [e] - The native HTML input change event.
   * @param {ISelectCardState} [state] - The current state of the select card component after the change.
   * @returns {void}
   */
  onChange: (
    e?: ChangeEvent<HTMLInputElement>,
    state?: ISelectCardState,
    options?: ISelectCardOnChangeOptions,
  ) => void;

  /**
   * Sets how many cards the user can select, either 'single' (radio buttons) or 'multiple' (checkboxes)
   */
  selectionMode: "single" | "multiple";

  /**
   * Used internally to create a UUID for the HTML `name` property on radios.
   */
  radioName: string;

  /**
   * Creates a set of ids that are selected.
   */
  selection: Set<string | number>;
}

/**
 * Props for the SelectCardProvider component
 * @extends Omit<ISelectCardContext, "radioName" | "selection">
 */
interface ISelectCardProviderProps
  extends Omit<ISelectCardContext, "radioName" | "selection"> {
  children: ReactNode;
}

const SelectCardContext = createContext<ISelectCardContext | null>(null);

/**
 * SelectCardProvider component for managing selection state across SelectCard components.
 *
 * Features:
 * - Provides context for SelectCard components to share selection state
 * - Manages selection mode (single vs multiple)
 * - Handles selection state updates and callbacks
 * - Generates unique radio button names for single selection mode
 * - Maintains a set of selected card IDs
 * - Integrates with SelectCardGroup for automatic context provision
 *
 * @example
 * <SelectCardProvider
 *   selectionMode="multiple"
 *   onChange={(e, state) => console.log('Selection changed:', state)}
 * >
 *   <SelectCard id="1" onChange={() => {}}>Option 1</SelectCard>
 *   <SelectCard id="2" onChange={() => {}}>Option 2</SelectCard>
 * </SelectCardProvider>
 */
export const SelectCardProvider = ({
  children,
  onChange,
  selectionMode,
}: ISelectCardProviderProps) => {
  const [selection, setSelection] = useState<Set<string | number>>(new Set());

  const radioName = useId();

  const handleChange = (
    e?: ChangeEvent<HTMLInputElement>,
    state?: ISelectCardState,
    options?: ISelectCardOnChangeOptions,
  ) => {
    if (!state || !state.id) {
      if (!options?.internal) {
        onChange(e, state);
      }

      return;
    }

    // Set internal selection
    if (selectionMode === "single") {
      if (state.checked) {
        setSelection(new Set([state.id]));
      } else {
        setSelection(new Set());
      }
    } else {
      if (state.checked) {
        setSelection(selection.add(state.id));
      } else {
        selection.delete(state.id);
        setSelection(selection);
      }
    }

    if (!options?.internal) {
      onChange(e, state);
    }
  };

  const context = {
    selectionMode,
    onChange: handleChange,
    radioName,
    selection,
  };

  return (
    <SelectCardContext.Provider value={context}>
      {children}
    </SelectCardContext.Provider>
  );
};

/**
 * Custom hook for accessing the SelectCard context.
 * @returns The SelectCard context or null if not within a SelectCardProvider
 */
export const useSelectCardContext = () => useContext(SelectCardContext);
